import { faChevronDown, faCircleExclamation, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRef, useState, useEffect } from 'react';
import cn from 'classnames';
import styles from 'components/Dropdown/Dropdown.scss';

export const Dropdown = ({
  className,
  icon,
  options = [],
  placeholder = 'Оберіть значення',
  value,
  onChange,
  errorText,
  invalid,
  tableCellStyle,
  readonly,
  clearable = true,
}) => {
  const [internalValue, setInternalValue] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selected = value ?? internalValue;
  const setSelected = onChange ?? setInternalValue;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    setSelected(option);
    setIsOpen(false);
  };

  const handleReset = (e) => {
    e.stopPropagation();
    setSelected(null);
    setIsOpen(false);
  };

  return (
    <div
      className={cn(styles.dropdown, className, {
        [styles.tableCell]: tableCellStyle,
        [styles.invalid]: invalid,
      })}
      ref={dropdownRef}
    >
      <div className={styles.buttonArea}>
        <button
          type='button'
          className={cn(styles.dropdownButton, {
            [styles.open]: isOpen,
            [styles.filled]: selected,
          })}
          onClick={() => !readonly && setIsOpen((prev) => !prev)}
          aria-expanded={isOpen}
          aria-haspopup='listbox'
          aria-controls='dropdown-list'
        >
          <span className={cn(styles.text, { [styles.placeholder]: !selected })}>
            {(icon || selected?.icon) &&
              (selected?.faIcon ? (
                <FontAwesomeIcon
                  className={cn(styles[selected?.iconColor], {
                    [styles.icon]: !selected?.icon,
                    [styles.roleIcon]: !selected?.icon,
                    [styles.optionIcon]: selected?.icon,
                  })}
                  icon={icon || selected.icon}
                />
              ) : (
                <img src={selected?.icon} />
              ))}
            <span>{selected?.label || placeholder}</span>
          </span>
          {!readonly && (
            <FontAwesomeIcon icon={faChevronDown} className={cn(styles.arrow, { [styles.open]: isOpen })} />
          )}
          <FontAwesomeIcon className={cn(styles.icon, styles.error)} icon={faCircleExclamation} />
        </button>
        {!readonly && clearable && selected && (
          <button
            type='button'
            onClick={handleReset}
            className={cn(styles.icon, styles.clearIcon, styles.clearButton)}
            title='Скинути вибір'
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        )}

        <span className={cn(styles.error, styles.errorText)}>{errorText}</span>
      </div>

      {options.length > 0 && (
        <div className={cn(styles.dropdownList, { [styles.open]: isOpen })}>
          {options.map((option) => (
            <button
              type='button'
              role='option'
              className={cn(styles.dropdownItem, {
                [styles.selectedItem]: option.value === selected?.value,
              })}
              onClick={() => handleSelect(option)}
              aria-selected={option.value === selected?.value}
              tabIndex={isOpen && option.value !== selected?.value ? 0 : -1}
              key={option.value}
            >
              {option?.icon &&
                (option?.faIcon ? (
                  <FontAwesomeIcon className={cn(styles.optionIcon, styles[option?.iconColor])} icon={option.icon} />
                ) : (
                  <img className={styles.optionIcon} src={option?.icon} />
                ))}
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
